import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../helpers/api_helper';

// Types
export interface User {
  id: number;
  mobile_number: string;
  first_name: string;
  current_points: number;
}

export interface Market {
  id: number;
  name: string;
  current_status: string;
  open_pana: string | null;
  open_digit: string | null;
  close_pana: string | null;
  close_digit: string | null;
  jodi_digit: string | null;
}

export interface BetType {
  id: number;
  name: string;
}

export interface AppBetType {
  id: number;
  payout_multiplier: number;
  payout_description: string;
  min_amount: number;
  max_amount: number;
}

export interface PlacedBet {
  open_pana?: string | number | null;
  open_digit?: string | number | null;
  close_pana?: string | number | null;
  close_digit?: string | number | null;
  pana?: string | number | null;
  digit?: string | number | null;
  jodi?: string | number | null;
  combination?: string | null;
  points?: number | string | null;
  bet_type?: string | null;
  sangam_type?: string | null;
  [key: string]: unknown;
}

export interface ResultData {
  raw_result?: string;
  settlement_result?: string;
  settlement_timestamp?: string;
  bet_code?: string;
  data_lost?: boolean;
  message?: string;
  [key: string]: unknown;
}

export interface BetDetails {
  session: string | null;
  open_pana: string | number | null;
  open_digit: string | number | null;
  close_pana: string | number | null;
  close_digit: string | number | null;
  jodi: string | number | null;
  points: number | string | null;
  bet_type: string | null;
  placed_bets: PlacedBet[];
  amount: number;
  before_amount: number;
  after_amount: number;
  potential_payout: number;
  bet_status: string;
  result_data: ResultData;
}

export interface CalculatedValues {
  payout_multiplier: number;
  win_amount: number;
  net_profit: number;
  roi_percentage: number;
}

export interface Timestamps {
  created_at: string;
  updated_at: string;
}

export interface BetHistory {
  id: number;
  user: User;
  market: Market;
  bet_type: BetType;
  app_bet_type: AppBetType;
  bet_details: BetDetails;
  calculated_values: CalculatedValues;
  timestamps: Timestamps;
}

export interface Summary {
  total_bets: number;
  won_bets: number;
  lost_bets: number;
  pending_bets: number;
  cancelled_bets: number;
  win_rate: number;
  total_wagered: number;
  total_won: number;
  net_profit: number;
  roi_percentage: number;
}

export interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  has_next: boolean;
  has_previous: boolean;
  page_size: number;
}

export interface Filters {
  search: string;
  market_id: string;
  bet_type_id: string;
  bet_status: string;
  session_type: string;
  user_id: string;
  date_from: string;
  date_to: string;
}

export interface BetHistoryApiResponse {
  success: boolean;
  data: BetHistory[];
  summary: Summary;
  pagination: Pagination;
  filters: Filters;
}

export interface BetHistoryState {
  bets: BetHistory[];
  summary: Summary | null;
  pagination: Pagination | null;
  filters: Filters;
  loading: boolean;
  error: string | null;
  success: string | null;
}

const initialState: BetHistoryState = {
  bets: [],
  summary: null,
  pagination: null,
  filters: {
    search: '',
    market_id: '',
    bet_type_id: '',
    bet_status: '',
    session_type: '',
    user_id: '',
    date_from: '',
    date_to: ''
  },
  loading: false,
  error: null,
  success: null,
};

// Async thunks
export const fetchBetHistory = createAsyncThunk(
  'starlineBetHistory/fetchBetHistory',
  async (params: {
    page?: number;
    page_size?: number;
    search?: string;
    market_id?: string;
    bet_type_id?: string;
    bet_status?: string;
    session_type?: string;
    user_id?: string;
    date_from?: string;
    date_to?: string;
  } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.page_size) queryParams.append('page_size', params.page_size.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.market_id) queryParams.append('market_id', params.market_id);
      if (params.bet_type_id) queryParams.append('bet_type_id', params.bet_type_id);
      if (params.bet_status) queryParams.append('bet_status', params.bet_status);
      if (params.session_type) queryParams.append('session_type', params.session_type);
      if (params.user_id) queryParams.append('user_id', params.user_id);
      if (params.date_from) queryParams.append('date_from', params.date_from);
      if (params.date_to) queryParams.append('date_to', params.date_to);

      const url = `/starline/history/${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await api.get(url);
      console.log('Bet History API Response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Bet History API Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

const betHistorySlice = createSlice({
  name: 'starlineBetHistory',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    setFilters: (state, action: PayloadAction<Partial<Filters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch bet history
    builder
      .addCase(fetchBetHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBetHistory.fulfilled, (state, action) => {
        state.loading = false;
        const response = action.payload as BetHistoryApiResponse & {
          bets?: BetHistory[];
        };
        state.bets = response.bets || response.data || [];
        state.summary = response.summary || null;
        state.pagination = response.pagination || null;
        state.filters = response.filters || state.filters;
      })
      .addCase(fetchBetHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSuccess, setFilters, resetState } = betHistorySlice.actions;
export default betHistorySlice.reducer;
